<template>
  <div class="tax-manager">
    <!-- Tax List -->
    <div class="mb-3">
      <h6 class="mb-2">Impuestos Aplicados</h6>
      <b-table
        v-if="taxes && taxes.length > 0"
        :items="taxes"
        :fields="taxFields"
        small
        striped
        hover
        responsive
      >
        <template #cell(type)="row">
          <b-badge :variant="getTaxTypeVariant(row.item)">
            {{ getTaxTypeLabel(row.item) }}
          </b-badge>
        </template>

        <template #cell(name)="row">
          <div v-if="row.item.is_manual">
            <strong>{{ row.item.tax_name }}</strong>
            <br>
            <small class="text-muted">{{ row.item.tax_type }}</small>
          </div>
          <div v-else>
            <strong>{{ row.item.tax_catalog?.name }}</strong>
            <br>
            <small class="text-muted">{{ row.item.tax_catalog_line?.rate_name }}</small>
          </div>
        </template>

        <template #cell(base)="row">
          ${{ formatCurrency(row.item.tax_base_amount) }}
        </template>

        <template #cell(rate)="row">
          <span :class="getRateClass(row.item)">
            {{ formatRate(row.item) }}
          </span>
        </template>

        <template #cell(amount)="row">
          <strong :class="getAmountClass(row.item)">
            ${{ formatCurrency(row.item.tax_amount) }}
          </strong>
        </template>

        <template #cell(notes)="row">
          <small v-if="row.item.notes" class="text-muted">
            {{ row.item.notes }}
          </small>
        </template>

        <template #cell(actions)="row">
          <b-button
            v-if="!readonly"
            size="sm"
            variant="outline-primary"
            @click="editTax(row.item)"
            class="mr-1"
          >
            <b-icon icon="pencil"></b-icon>
          </b-button>
          <b-button
            v-if="!readonly"
            size="sm"
            variant="outline-danger"
            @click="confirmDeleteTax(row.item)"
          >
            <b-icon icon="trash"></b-icon>
          </b-button>
        </template>
      </b-table>

      <b-alert v-else show variant="info" class="mb-0">
        No hay impuestos aplicados a este artículo
      </b-alert>
    </div>

    <!-- Add/Edit Tax Buttons -->
    <div v-if="!readonly && !showTaxForm" class="mb-3">
      <b-button size="sm" variant="primary" @click="openAddCatalogTax" class="mr-2">
        <b-icon icon="plus"></b-icon> Impuesto del Catálogo
      </b-button>
      <b-button size="sm" variant="success" @click="openAddManualTax" class="mr-2">
        <b-icon icon="plus"></b-icon> Impuesto Manual
      </b-button>
    </div>

    <!-- Tax Form (Add/Edit) -->
    <b-card v-if="showTaxForm" class="mb-3">
      <template #header>
        <h6 class="mb-0">
          {{ editingTax ? 'Editar Impuesto' : (taxFormMode === 'catalog' ? 'Agregar Impuesto del Catálogo' : 'Agregar Impuesto Manual') }}
        </h6>
      </template>

      <!-- Catalog Tax Form -->
      <div v-if="taxFormMode === 'catalog'">
        <b-form-group label="Catálogo de Impuestos" label-for="tax-catalog">
          <b-form-select
            id="tax-catalog"
            v-model="taxForm.tax_catalog_id"
            :options="taxCatalogOptions"
            @change="onTaxCatalogChange"
            :disabled="editingTax"
          >
            <template #first>
              <b-form-select-option :value="null">-- Seleccionar Catálogo --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>

        <b-form-group label="Tasa de Impuesto" label-for="tax-line">
          <b-form-select
            id="tax-line"
            v-model="taxForm.tax_catalog_line_id"
            :options="taxLineOptions"
            :disabled="!taxForm.tax_catalog_id || editingTax"
          >
            <template #first>
              <b-form-select-option :value="null">-- Seleccionar Tasa --</b-form-select-option>
            </template>
          </b-form-select>
        </b-form-group>
      </div>

      <!-- Manual Tax Form -->
      <div v-if="taxFormMode === 'manual'">
        <b-form-group label="Nombre del Impuesto *" label-for="tax-name">
          <b-form-input
            id="tax-name"
            v-model="taxForm.tax_name"
            placeholder="Ej: IEPS Especial, Retención ISR, Cargo por manejo"
            required
          ></b-form-input>
        </b-form-group>

        <b-form-group label="Tipo de Impuesto *" label-for="tax-type">
          <b-form-select
            id="tax-type"
            v-model="taxForm.tax_type"
          >
            <b-form-select-option value="percentage">Porcentaje (%)</b-form-select-option>
            <b-form-select-option value="fixed">Monto Fijo ($)</b-form-select-option>
            <b-form-select-option value="withholding">Retención (-)</b-form-select-option>
          </b-form-select>
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group
              :label="taxForm.tax_type === 'fixed' ? 'Monto Fijo *' : 'Tasa (%) *'"
              label-for="tax-rate"
            >
              <b-form-input
                id="tax-rate"
                v-model.number="taxForm.tax_rate"
                type="number"
                step="0.01"
                :placeholder="taxForm.tax_type === 'fixed' ? 'Ej: 50.00' : 'Ej: 8.00'"
                @input="calculateManualTaxAmount"
                required
              ></b-form-input>
              <small v-if="taxForm.tax_type === 'withholding'" class="text-muted">
                Use valores negativos para retenciones (Ej: -4.00)
              </small>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Monto del Impuesto *" label-for="tax-amount">
              <b-form-input
                id="tax-amount"
                v-model.number="taxForm.tax_amount"
                type="number"
                step="0.01"
                :readonly="taxForm.tax_type !== 'fixed'"
                required
              ></b-form-input>
              <small v-if="taxForm.tax_type === 'percentage'" class="text-muted">
                Calculado automáticamente
              </small>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Notas" label-for="tax-notes">
          <b-form-textarea
            id="tax-notes"
            v-model="taxForm.notes"
            placeholder="Información adicional sobre este impuesto..."
            rows="2"
          ></b-form-textarea>
        </b-form-group>
      </div>

      <!-- Common Fields -->
      <b-row>
        <b-col md="6">
          <b-form-group label="Monto Base *" label-for="tax-base">
            <b-form-input
              id="tax-base"
              v-model.number="taxForm.tax_base_amount"
              type="number"
              step="0.01"
              @input="calculateTaxAmount"
              required
            ></b-form-input>
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Secuencia *" label-for="tax-sequence">
            <b-form-input
              id="tax-sequence"
              v-model.number="taxForm.sequence"
              type="number"
              min="1"
              required
            ></b-form-input>
            <small class="text-muted">Orden de aplicación del impuesto</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group>
        <b-form-checkbox v-model="taxForm.is_included_in_price">
          El impuesto está incluido en el precio
        </b-form-checkbox>
      </b-form-group>

      <!-- Form Actions -->
      <div class="d-flex justify-content-end">
        <b-button size="sm" variant="secondary" @click="cancelTaxForm" class="mr-2">
          Cancelar
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          @click="saveTax"
          :disabled="!isTaxFormValid"
        >
          {{ editingTax ? 'Actualizar' : 'Agregar' }}
        </b-button>
      </div>
    </b-card>

    <!-- Tax Summary -->
    <b-card v-if="taxes && taxes.length > 0" bg-variant="light" class="mt-3">
      <b-row>
        <b-col md="6">
          <div class="d-flex justify-content-between">
            <span>Subtotal Base:</span>
            <strong>${{ formatCurrency(totalBase) }}</strong>
          </div>
        </b-col>
        <b-col md="6">
          <div class="d-flex justify-content-between">
            <span>Total Impuestos:</span>
            <strong :class="totalTaxAmount >= 0 ? 'text-success' : 'text-danger'">
              ${{ formatCurrency(totalTaxAmount) }}
            </strong>
          </div>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'PurchaseOrderItemTaxManager',

  props: {
    purchaseOrderId: {
      type: Number,
      required: true
    },
    itemId: {
      type: Number,
      required: true
    },
    baseAmount: {
      type: Number,
      default: 0
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      taxes: [],
      taxCatalogs: [],
      showTaxForm: false,
      taxFormMode: 'catalog', // 'catalog' or 'manual'
      editingTax: null,
      taxForm: this.getEmptyTaxForm(),
      loading: false,
      taxFields: [
        { key: 'type', label: 'Tipo', tdClass: 'align-middle' },
        { key: 'name', label: 'Impuesto', tdClass: 'align-middle' },
        { key: 'base', label: 'Base', tdClass: 'align-middle text-right' },
        { key: 'rate', label: 'Tasa', tdClass: 'align-middle text-right' },
        { key: 'amount', label: 'Monto', tdClass: 'align-middle text-right' },
        { key: 'sequence', label: 'Seq', tdClass: 'align-middle text-center' },
        { key: 'notes', label: 'Notas', tdClass: 'align-middle' },
        { key: 'actions', label: '', tdClass: 'align-middle text-right' }
      ]
    };
  },

  computed: {
    taxCatalogOptions() {
      return this.taxCatalogs.map(cat => ({
        value: cat.id,
        text: `${cat.name} (${cat.tax_code})`
      }));
    },

    taxLineOptions() {
      if (!this.taxForm.tax_catalog_id) return [];
      const catalog = this.taxCatalogs.find(c => c.id === this.taxForm.tax_catalog_id);
      if (!catalog || !catalog.tax_lines) return [];

      return catalog.tax_lines.map(line => ({
        value: line.id,
        text: `${line.rate_name} - ${line.rate_value}%`,
        rate: line.rate_value
      }));
    },

    isTaxFormValid() {
      if (this.taxFormMode === 'catalog') {
        return this.taxForm.tax_catalog_id && this.taxForm.tax_catalog_line_id &&
               this.taxForm.tax_base_amount > 0 && this.taxForm.sequence > 0;
      } else {
        return this.taxForm.tax_name && this.taxForm.tax_type &&
               this.taxForm.tax_base_amount > 0 && this.taxForm.sequence > 0 &&
               this.taxForm.tax_rate != null && this.taxForm.tax_amount != null;
      }
    },

    totalBase() {
      if (!this.taxes || this.taxes.length === 0) return 0;
      return this.taxes[0]?.tax_base_amount || 0;
    },

    totalTaxAmount() {
      return this.taxes.reduce((sum, tax) => sum + (tax.tax_amount || 0), 0);
    }
  },

  mounted() {
    this.loadTaxes();
    this.loadTaxCatalogs();
  },

  methods: {
    async loadTaxes() {
      try {
        this.loading = true;
        const response = await axios.get(
          `/purchase-orders/${this.purchaseOrderId}/items/${this.itemId}/taxes`
        );
        this.taxes = response.data || [];
      } catch (error) {
        console.error('Error loading taxes:', error);
        this.$emit('error', 'Error al cargar los impuestos');
      } finally {
        this.loading = false;
      }
    },

    async loadTaxCatalogs() {
      try {
        const response = await axios.get('/tax-catalogs');
        this.taxCatalogs = response.data || [];

        // Load tax lines for each catalog
        for (let catalog of this.taxCatalogs) {
          try {
            const linesResponse = await axios.get(`/tax-catalogs/${catalog.id}`);
            catalog.tax_lines = linesResponse.data.tax_lines || [];
          } catch (err) {
            console.error(`Error loading tax lines for catalog ${catalog.id}:`, err);
            catalog.tax_lines = [];
          }
        }
      } catch (error) {
        console.error('Error loading tax catalogs:', error);
      }
    },

    openAddCatalogTax() {
      this.taxFormMode = 'catalog';
      this.editingTax = null;
      this.taxForm = this.getEmptyTaxForm();
      this.taxForm.tax_base_amount = this.baseAmount;
      this.taxForm.sequence = this.getNextSequence();
      this.showTaxForm = true;
    },

    openAddManualTax() {
      this.taxFormMode = 'manual';
      this.editingTax = null;
      this.taxForm = this.getEmptyTaxForm();
      this.taxForm.tax_base_amount = this.baseAmount;
      this.taxForm.sequence = this.getNextSequence();
      this.taxForm.tax_type = 'percentage';
      this.showTaxForm = true;
    },

    editTax(tax) {
      this.editingTax = tax;
      this.taxFormMode = tax.is_manual ? 'manual' : 'catalog';
      this.taxForm = { ...tax };
      this.showTaxForm = true;
    },

    cancelTaxForm() {
      this.showTaxForm = false;
      this.editingTax = null;
      this.taxForm = this.getEmptyTaxForm();
    },

    async saveTax() {
      try {
        const payload = this.prepareTaxPayload();

        if (this.editingTax) {
          await axios.put(
            `/purchase-orders/${this.purchaseOrderId}/items/${this.itemId}/taxes/${this.editingTax.id}`,
            payload
          );
          this.$emit('success', 'Impuesto actualizado correctamente');
        } else {
          await axios.post(
            `/purchase-orders/${this.purchaseOrderId}/items/${this.itemId}/taxes`,
            payload
          );
          this.$emit('success', 'Impuesto agregado correctamente');
        }

        await this.loadTaxes();
        this.cancelTaxForm();
        this.$emit('tax-updated');
      } catch (error) {
        console.error('Error saving tax:', error);
        this.$emit('error', error.response?.data?.error || 'Error al guardar el impuesto');
      }
    },

    async confirmDeleteTax(tax) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `¿Está seguro de eliminar el impuesto "${this.getTaxDisplayName(tax)}"?`,
        {
          title: 'Confirmar Eliminación',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Eliminar',
          cancelTitle: 'Cancelar',
          footerClass: 'p-2',
          hideHeaderClose: false
        }
      );

      if (confirmed) {
        await this.deleteTax(tax.id);
      }
    },

    async deleteTax(taxId) {
      try {
        await axios.delete(
          `/purchase-orders/${this.purchaseOrderId}/items/${this.itemId}/taxes/${taxId}`
        );
        await this.loadTaxes();
        this.$emit('success', 'Impuesto eliminado correctamente');
        this.$emit('tax-updated');
      } catch (error) {
        console.error('Error deleting tax:', error);
        this.$emit('error', 'Error al eliminar el impuesto');
      }
    },

    onTaxCatalogChange() {
      this.taxForm.tax_catalog_line_id = null;
      this.taxForm.tax_rate = 0;
      this.taxForm.tax_amount = 0;
    },

    calculateTaxAmount() {
      const lineOption = this.taxLineOptions.find(
        opt => opt.value === this.taxForm.tax_catalog_line_id
      );

      if (lineOption && this.taxForm.tax_base_amount) {
        this.taxForm.tax_rate = lineOption.rate;
        this.taxForm.tax_amount = (this.taxForm.tax_base_amount * lineOption.rate) / 100;
      }
    },

    calculateManualTaxAmount() {
      if (this.taxForm.tax_type === 'fixed') {
        this.taxForm.tax_amount = this.taxForm.tax_rate;
      } else if (this.taxForm.tax_type === 'percentage' || this.taxForm.tax_type === 'withholding') {
        if (this.taxForm.tax_base_amount && this.taxForm.tax_rate != null) {
          this.taxForm.tax_amount = (this.taxForm.tax_base_amount * this.taxForm.tax_rate) / 100;
        }
      }
    },

    prepareTaxPayload() {
      const payload = {
        tax_base_amount: this.taxForm.tax_base_amount,
        tax_rate: this.taxForm.tax_rate,
        tax_amount: this.taxForm.tax_amount,
        sequence: this.taxForm.sequence,
        is_included_in_price: this.taxForm.is_included_in_price || false,
        is_manual: this.taxFormMode === 'manual'
      };

      if (this.taxFormMode === 'catalog') {
        payload.tax_catalog_id = this.taxForm.tax_catalog_id;
        payload.tax_catalog_line_id = this.taxForm.tax_catalog_line_id;
      } else {
        // Manual tax
        payload.tax_name = this.taxForm.tax_name;
        payload.tax_type = this.taxForm.tax_type;
        payload.notes = this.taxForm.notes || '';
      }

      return payload;
    },

    getEmptyTaxForm() {
      return {
        tax_catalog_id: null,
        tax_catalog_line_id: null,
        tax_name: '',
        tax_type: 'percentage',
        tax_base_amount: 0,
        tax_rate: 0,
        tax_amount: 0,
        sequence: 1,
        is_included_in_price: false,
        notes: ''
      };
    },

    getNextSequence() {
      if (!this.taxes || this.taxes.length === 0) return 1;
      return Math.max(...this.taxes.map(t => t.sequence)) + 1;
    },

    getTaxTypeVariant(tax) {
      if (tax.is_manual) {
        if (tax.tax_type === 'withholding') return 'warning';
        if (tax.tax_type === 'fixed') return 'info';
        return 'success';
      }
      return 'primary';
    },

    getTaxTypeLabel(tax) {
      if (tax.is_manual) {
        return tax.tax_type === 'withholding' ? 'Retención' :
               tax.tax_type === 'fixed' ? 'Fijo' : 'Manual';
      }
      return 'Catálogo';
    },

    getTaxDisplayName(tax) {
      return tax.is_manual ? tax.tax_name : tax.tax_catalog?.name;
    },

    getRateClass(tax) {
      if (tax.tax_type === 'withholding' || tax.tax_rate < 0) {
        return 'text-warning';
      }
      return '';
    },

    getAmountClass(tax) {
      if (tax.tax_amount < 0) {
        return 'text-warning';
      }
      return 'text-success';
    },

    formatRate(tax) {
      if (tax.tax_type === 'fixed') {
        return `$${this.formatCurrency(tax.tax_rate)}`;
      }
      return `${tax.tax_rate.toFixed(2)}%`;
    },

    formatCurrency(value) {
      return (value || 0).toFixed(2);
    }
  }
};
</script>

<style scoped>
.tax-manager {
  padding: 1rem;
}

.tax-manager h6 {
  font-weight: 600;
  color: #333;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-warning {
  color: #ffc107 !important;
}
</style>
